import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, MOTION_QUERIES } from '@/lib/motion'

interface MagneticProps {
    children: ReactNode
    strength?: number
    className?: string
}

// Pulls its content towards the pointer while hovered and springs back on leave.
const Magnetic: FC<MagneticProps> = ({ children, strength = 0.35, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const element = ref.current

        if (!element || !window.matchMedia(MOTION_QUERIES.fine).matches) return

        const moveX = gsap.quickTo(element, 'x', { duration: 0.6, ease: 'power3.out' })
        const moveY = gsap.quickTo(element, 'y', { duration: 0.6, ease: 'power3.out' })

        const onMove = (event: PointerEvent): void => {
            const rect = element.getBoundingClientRect()
            moveX((event.clientX - (rect.left + rect.width / 2)) * strength)
            moveY((event.clientY - (rect.top + rect.height / 2)) * strength)
        }

        const onLeave = (): void => {
            moveX(0)
            moveY(0)
        }

        element.addEventListener('pointermove', onMove)
        element.addEventListener('pointerleave', onLeave)

        return () => {
            element.removeEventListener('pointermove', onMove)
            element.removeEventListener('pointerleave', onLeave)
        }
    }, [strength])

    return (
        <span ref={ref} className={cn('inline-block will-change-transform', className)}>
            {children}
        </span>
    )
}

export default Magnetic
